import type { ReferenceRow } from './portal';
import { isDone, progress } from './steps.completion';
import type { CompletionStage, NormalStep } from './steps.types';

// Mirrors Globus's shared REFERENCE_KEY_PATTERNS (isReferenceStepKey): these
// step keys render the reference-management cards instead of a form-engine
// step — the step's "answers" are Globus reference_submissions rows, not
// stepData, and the step auto-completes on approved references.
const REFERENCE_KEY_PATTERNS = [
  /^reference_details/,
  /^professional_reference/,
  /^academic_reference/,
  /^personal_reference/,
  /^supervisor_reference/,
  /^custom_reference/,
  /^reference_letter/,
  /^reference_submission/,
  /^references$/,
];

export function isReferenceStep(stepKey: string | undefined | null): boolean {
  if (!stepKey) return false;
  return REFERENCE_KEY_PATTERNS.some((p) => p.test(stepKey));
}

/** How many references the applicant must submit for their part to count as
 *  "done" — mirrors ReferenceDetailsStep's REQUIRED_SLOTS. The reference step
 *  itself only auto-completes in Globus once that many references are APPROVED,
 *  which can lag days behind the applicant finishing everything they can do. */
export const REQUIRED_REFERENCES = 2;

/** Reference rows representing a referee the applicant has actually invited (vs
 *  an empty slot, an expired request, or a superseded/revoked row). Lets us
 *  tell "hasn't asked anyone yet" apart from "asked, awaiting response/approval". */
export function countSubmittedReferees(rows: ReferenceRow[]): number {
  return rows.filter((r) => ['invited', 'reminded', 'completed'].includes(r.status)).length;
}

/** Every non-reference step is complete and the only thing still open is one or
 *  more reference steps — i.e. the applicant has nothing left to do except wait
 *  on their referees / staff approval. */
export function nonReferenceComplete(steps: NormalStep[]): boolean {
  const refs = steps.filter((s) => isReferenceStep(s.stepKey));
  const others = steps.filter((s) => !isReferenceStep(s.stepKey));
  return others.length > 0 && others.every(isDone) && refs.length > 0 && refs.some((s) => !isDone(s));
}

/**
 * Two-stage applicant completion signal (drives the success message):
 *   'complete'            — every step, INCLUDING the async reference step, is
 *                           done in Globus (pct === 100).
 *   'awaiting_references' — the applicant has finished all their own work AND
 *                           submitted the required referees, but the reference
 *                           step is still pending referee response / approval.
 *                           They've done their part; we're collecting refs.
 *   'none'                — steps still remain for the applicant to do.
 *
 * The reference step deliberately does NOT short-circuit to complete on referee
 * submission (Globus only flips it on APPROVED references) — so stage 1 exists
 * to acknowledge the applicant at the moment THEY finish, regardless of how long
 * referees/staff take. `refereesSubmitted` = countSubmittedReferees >= REQUIRED_REFERENCES.
 */
export function completionStage(steps: NormalStep[], refereesSubmitted: boolean): CompletionStage {
  if (steps.length === 0) return 'none';
  if (progress(steps).pct === 100) return 'complete';
  if (refereesSubmitted && nonReferenceComplete(steps)) return 'awaiting_references';
  return 'none';
}
